import { useReducer, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/claim-form-editor/ require four levels up to reach convex/_generated/
import type { Id } from '../../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: same directory depth as api import above
import { useOrgContext } from '@/hooks/useOrgContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Save, Plus } from 'lucide-react';
import { toast } from 'sonner';
import type {
  IClaimFormSectionConfig,
  IClaimFormCustomField,
} from '@/@types/claims';
import { editorReducer } from './editorReducer';
import { useClaimFormEditorState } from './useClaimFormEditorState';
import { SectionList } from './SectionList';
import { CustomFieldForm } from './CustomFieldForm';

export function ClaimFormEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentOrg } = useOrgContext();

  const isCreateMode = id === 'new';
  const template = useQuery(
    api.insurance.claimFormTemplates.get,
    id && !isCreateMode ? { templateId: id as Id<'claimFormTemplates'> } : 'skip' // AS_JUSTIFIED: one-time Id cast from route params
  );
  const orgTypes = useQuery(
    api.insurance.types.listOrgTypes,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );
  const existingTemplates = useQuery(
    api.insurance.claimFormTemplates.list,
    currentOrg && isCreateMode ? { orgId: currentOrg._id } : 'skip'
  );
  const updateTemplate = useMutation(api.insurance.claimFormTemplates.update);
  const createTemplate = useMutation(api.insurance.claimFormTemplates.create);

  const {
    templateName,
    setTemplateName,
    isDefault,
    setIsDefault,
    selectedTypeId,
    setSelectedTypeId,
    isSaving,
    setIsSaving,
    initFromTemplate,
  } = useClaimFormEditorState();

  const [editorState, dispatch] = useReducer(editorReducer, {
    sections: [],
    customFields: [],
  });

  const customFieldSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (!template) return;
    initFromTemplate(template);
    dispatch({ type: 'SET_SECTIONS', sections: template.sections as IClaimFormSectionConfig[] });
    dispatch({ type: 'SET_CUSTOM_FIELDS', customFields: (template.customFields ?? []) as IClaimFormCustomField[] });
    // initFromTemplate is a stable setter wrapper, intentionally excluded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template]);

  if (!isCreateMode && template === undefined) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-60 w-full" />
      </div>
    );
  }

  if (!isCreateMode && template === null) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <p className="text-muted-foreground">Template not found.</p>
      </div>
    );
  }

  async function handleSave() {
    if (!id || !currentOrg) return;
    setIsSaving(true);
    try {
      if (isCreateMode) {
        await createTemplate({
          orgId: currentOrg._id,
          name: templateName || 'New Template',
          templateCategory: 'custom',
          sections: editorState.sections,
          customFields: editorState.customFields,
          isDefault,
          ...(selectedTypeId !== 'none' && { insuranceTypeId: selectedTypeId as Id<'orgInsuranceTypes'> }), // AS_JUSTIFIED: one-time Id cast from Select value
        });
      } else {
        await updateTemplate({
          templateId: id as Id<'claimFormTemplates'>, // AS_JUSTIFIED: one-time Id cast from route params
          name: templateName,
          sections: editorState.sections,
          customFields: editorState.customFields,
          isDefault,
          ...(selectedTypeId !== 'none' && { insuranceTypeId: selectedTypeId as Id<'orgInsuranceTypes'> }), // AS_JUSTIFIED: one-time Id cast from Select value
        });
      }
      toast.success(isCreateMode ? 'Template created' : 'Template saved');
      navigate('/insurance/claim-forms');
    } catch {
      toast.error('Failed to save template');
    } finally {
      setIsSaving(false);
    }
  }

  function handleAddCustomField() {
    const newField: IClaimFormCustomField = {
      id: `custom_${Date.now()}`,
      sectionKey: editorState.sections[0]?.sectionKey ?? '',
      label: 'New Field',
      type: 'text',
      required: false,
      sortOrder: editorState.customFields.length,
    };
    dispatch({ type: 'ADD_CUSTOM_FIELD', field: newField });
  }

  function handleCustomFieldDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = editorState.customFields.findIndex(f => f.id === active.id);
    const newIndex = editorState.customFields.findIndex(f => f.id === over.id);
    const newOrder = arrayMove(editorState.customFields.map(f => f.id), oldIndex, newIndex);
    dispatch({ type: 'REORDER_CUSTOM_FIELDS', fieldIds: newOrder });
  }

  const sectionOptions = editorState.sections.map(s => ({ key: s.sectionKey, label: s.label }));
  const typeList = orgTypes ?? [];

  return (
    <div className="min-h-full bg-background-secondary py-8 px-10">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/insurance/claim-forms')}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1 flex items-center gap-3">
          <Input
            value={templateName}
            onChange={e => setTemplateName(e.target.value)}
            className="max-w-sm font-semibold text-lg h-10"
          />
          <Badge variant="outline" className="capitalize">{isCreateMode ? 'custom' : template?.templateCategory}</Badge>
        </div>
        <Button onClick={() => void handleSave()} disabled={isSaving}>
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Template Settings</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-48">
            <Label className="text-xs text-muted-foreground">Insurance Type (optional)</Label>
            <Select value={selectedTypeId} onValueChange={setSelectedTypeId}>
              <SelectTrigger className="h-8 text-sm mt-1">
                <SelectValue placeholder="Any type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Any type</SelectItem>
                {typeList.map(t => (
                  <SelectItem key={t._id} value={t._id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2 pb-0.5">
            <Switch id="is-default" checked={isDefault} onCheckedChange={setIsDefault} />
            <Label htmlFor="is-default" className="cursor-pointer">Set as default template</Label>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Sections &amp; Fields</CardTitle>
            {isCreateMode && editorState.sections.length === 0 && existingTemplates && existingTemplates.length > 0 && (
              <Select onValueChange={(val) => {
                const base = existingTemplates.find(t => t._id === val);
                if (base) {
                  dispatch({ type: 'SET_SECTIONS', sections: base.sections as IClaimFormSectionConfig[] }); // AS_JUSTIFIED: Convex return sections narrowed to typed config
                }
              }}>
                <SelectTrigger className="w-56 h-8 text-sm">
                  <SelectValue placeholder="Copy from existing template..." />
                </SelectTrigger>
                <SelectContent>
                  {existingTemplates.map(t => (
                    <SelectItem key={t._id} value={t._id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {editorState.sections.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              No sections yet. {isCreateMode && existingTemplates && existingTemplates.length > 0
                ? 'Copy from an existing template above, or add a new section below.'
                : 'Add a section to get started.'}
            </p>
          )}
          <SectionList sections={editorState.sections} dispatch={dispatch} />
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => {
              const key = `section_${Date.now()}`;
              dispatch({
                type: 'ADD_SECTION',
                section: {
                  sectionKey: key,
                  label: 'New Section',
                  visible: true,
                  sortOrder: editorState.sections.length,
                  fields: [],
                },
              });
            }}
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            Add Section
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Custom Fields</CardTitle>
            <Button size="sm" variant="outline" onClick={handleAddCustomField}>
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Add Custom Field
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {editorState.customFields.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              No custom fields. Click &quot;Add Custom Field&quot; to add one.
            </p>
          )}
          <DndContext
            sensors={customFieldSensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleCustomFieldDragEnd}
          >
            <SortableContext
              items={editorState.customFields.map(f => f.id)}
              strategy={verticalListSortingStrategy}
            >
              {editorState.customFields.map(field => (
                <CustomFieldForm
                  key={field.id}
                  field={field}
                  sections={sectionOptions}
                  dispatch={dispatch}
                />
              ))}
            </SortableContext>
          </DndContext>
        </CardContent>
      </Card>
    </div>
  );
}
